import {
  ApplicationRef,
  ComponentRef,
  inject,
  Injectable,
  ViewContainerRef
} from '@angular/core';
import { AbstractWidgetInterface, WidgetParams } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';

@Injectable()
export class WidgetFactoryService {
  private readonly _logger = inject(NGXLogger);
  private _widgetHost: ViewContainerRef | null = null;
  private readonly _appRef = inject(ApplicationRef);

  private _widgetComponentRef?: ComponentRef<AbstractWidgetInterface>;

  set widgetHost(value: ViewContainerRef | null) {
    // If the host is changing, clean up existing widgets
    if (this._widgetHost !== value) {
      this._logger.debug('[WidgetFactory] Widget host changed, cleaning up');
      this.destroyWidget();
      this._widgetHost = value;
    }
  }

  get widgetHost(): ViewContainerRef | null {
    return this._widgetHost;
  }

  get widgetComponentRef(): ComponentRef<AbstractWidgetInterface> | undefined {
    return this._widgetComponentRef;
  }

  instantiateWidget(id?: string, widgetParams?: WidgetParams): boolean {
    if (!this._widgetHost) {
      this._logger.error('[WidgetFactory] Widget host not set');
      return false;
    }

    // Clean up any existing widget first
    this.destroyWidget();

    if (!id || !widgetParams) {
      this._logger.error(
        '[WidgetFactory] Missing required parameters for widget instantiation'
      );
      return false;
    }

    try {
      // Separate out the component for content. Not needed in params after this point.
      const { widgetComponent, ...paramsWithoutComponent } = widgetParams;

      if (!widgetComponent) {
        this._logger.error('[WidgetFactory] Widget component not provided');
        return false;
      }

      this._widgetComponentRef =
        this._widgetHost.createComponent<AbstractWidgetInterface>(
          widgetComponent,
          {
            environmentInjector: this._appRef.injector
          }
        );

      // Set widget properties
      Object.assign(this._widgetComponentRef.instance, {
        ...paramsWithoutComponent,
        id
      });

      // Trigger change detection
      this._widgetComponentRef.changeDetectorRef.detectChanges();

      return true;
    } catch (error) {
      this._logger.error('[WidgetFactory] Error instantiating widget:', error);
      this.destroyWidget();
      return false;
    }
  }

  destroyWidget(): void {
    try {
      if (this._widgetComponentRef) {
        // Destroy the component reference
        this._widgetComponentRef.destroy();

        // Clear the reference
        this._widgetComponentRef = undefined;

        // Clear the view container
        if (this._widgetHost) {
          this._widgetHost.clear();
        }
      }
    } catch (error) {
      this._logger.error('[WidgetFactory] Error destroying widget:', error);
    }
  }
}
